// frontend/src/components/Login.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackEvent, trackRedditEvent } from '../utils/analytics';
import { sendWelcomeEmail, saveTwitterHandle } from '../api/api';
import { usePageMeta } from '../hooks/usePageMeta';

enum AuthMode {
  SIGN_IN,
  SIGN_UP,
  RESET_PASSWORD
}

function friendlyAuthError(error: any, provider: string): string {
  const code = error?.code || '';
  if (code === 'auth/account-exists-with-different-credential') {
    return 'An account already exists with the same email address but a different sign-in method. Try signing in with Google or email instead.';
  }
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return '';  // user cancelled, no error needed
  }
  if (code === 'auth/popup-blocked') {
    return 'Sign-in popup was blocked by your browser. Please allow popups for this site.';
  }
  return error?.message || `Failed to sign in with ${provider}`;
}

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#1c1917" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58Z" fill="#EA4335" />
  </svg>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState<AuthMode>(AuthMode.SIGN_IN);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const { currentUser, signInWithGoogle, signInWithTwitter, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shareRef = searchParams.get('ref');
  const shareCode = searchParams.get('code');

  usePageMeta({
    title: 'Log In — My Bracket Sim',
    description: 'Sign in to My Bracket Sim to manage your bracket pools and run simulations.',
    path: '/login',
  });

  // Store share code in localStorage so AuthContext can claim it after auth completes
  useEffect(() => {
    if (shareRef === 'share' && shareCode) {
      localStorage.setItem('pendingShareCode', shareCode);
    }
  }, [shareRef, shareCode]);

  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const handleGoogleSignIn = async () => {
    setError('');
    setIsLoading(true);
    try {
      await signInWithGoogle();
      trackEvent('sign_up', { method: 'google' });
      trackRedditEvent('SignUp');
      sendWelcomeEmail();
    } catch (error: any) {
      const msg = friendlyAuthError(error, 'Google');
      if (msg) setError(msg);
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleTwitterSignIn = async () => {
    setError('');
    setIsLoading(true);
    try {
      const { handle, email: twitterEmail } = await signInWithTwitter();
      trackEvent('sign_up', { method: 'twitter' });
      trackRedditEvent('SignUp');
      sendWelcomeEmail(twitterEmail || undefined);
      if (handle) {
        saveTwitterHandle(handle);
      }
    } catch (error: any) {
      const msg = friendlyAuthError(error, 'X');
      if (msg) setError(msg);
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleEmailAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);
    try {
      switch (authMode) {
        case AuthMode.SIGN_IN:
          await signInWithEmail(email, password);
          sendWelcomeEmail();
          break;
        case AuthMode.SIGN_UP:
          await signUpWithEmail(email, password);
          trackEvent('sign_up', { method: 'email' });
          trackRedditEvent('SignUp');
          sendWelcomeEmail();
          break;
        case AuthMode.RESET_PASSWORD:
          await resetPassword(email);
          setMessage('Password reset email sent! Check your inbox.');
          break;
      }
    } catch (error: any) {
      const msg = friendlyAuthError(error, 'email');
      if (msg) setError(msg);
      console.error(error);
    }
    setIsLoading(false);
  };

  const title =
    authMode === AuthMode.SIGN_IN ? 'Sign In' :
    authMode === AuthMode.SIGN_UP ? 'Create Account' :
    'Reset Password';

  const submitLabel =
    authMode === AuthMode.SIGN_IN ? 'Sign In' :
    authMode === AuthMode.SIGN_UP ? 'Sign Up' :
    'Reset Password';

  return (
    <div style={s.page}>
      {/* Logo */}
      <a href="/" style={s.logoLink}>
        <img src="/bracketsim_header.png" alt="My Bracket Sim" style={s.logo} />
      </a>

      {/* Share referral banner */}
      {shareRef === 'share' && shareCode && (
        <div style={{
          backgroundColor: 'hsl(210, 40%, 96%)',
          border: '1px solid hsl(210, 40%, 85%)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          maxWidth: '380px',
          width: '100%',
          textAlign: 'center',
          fontSize: '14px',
          color: 'hsl(210, 20%, 30%)',
        }}>
          You were invited to view a shared scenario. Sign up to get a copy of the pool and build your own brackets!
        </div>
      )}

      {/* Card */}
      <div style={s.card}>
        <h2 style={s.title}>{title}</h2>

        {/* Google */}
        <button
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          style={s.googleBtn}
          className="login-google-btn"
        >
          <GoogleIcon />
          <span>Sign in with Google</span>
        </button>

        {/* Twitter/X */}
        <button
          onClick={handleTwitterSignIn}
          disabled={isLoading}
          style={{ ...s.googleBtn, marginTop: '8px' }}
          className="login-google-btn"
        >
          <XIcon />
          <span>Sign in with X</span>
        </button>

        {/* Divider */}
        <div style={s.divider}>
          <div style={s.dividerLine} />
          <span style={s.dividerText}>or</span>
          <div style={s.dividerLine} />
        </div>

        {/* Email form */}
        <form onSubmit={handleEmailAction} style={s.form}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={s.input}
            className="login-input"
          />

          {authMode !== AuthMode.RESET_PASSWORD && (
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={s.input}
              className="login-input"
            />
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...s.submitBtn,
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
            className="login-submit-btn"
          >
            {isLoading ? 'Loading...' : submitLabel}
          </button>
        </form>

        {error && <p style={s.error}>{error}</p>}
        {message && <p style={s.success}>{message}</p>}

        {/* Mode toggles */}
        <div style={s.links}>
          {authMode === AuthMode.SIGN_IN && (
            <>
              <button
                style={s.linkBtn}
                className="login-link"
                onClick={() => setAuthMode(AuthMode.SIGN_UP)}
              >
                Create Account
              </button>
              <button
                style={s.linkBtn}
                className="login-link"
                onClick={() => setAuthMode(AuthMode.RESET_PASSWORD)}
              >
                Forgot Password?
              </button>
            </>
          )}
          {authMode === AuthMode.SIGN_UP && (
            <button
              style={s.linkBtn}
              className="login-link"
              onClick={() => setAuthMode(AuthMode.SIGN_IN)}
            >
              Already have an account? Sign In
            </button>
          )}
          {authMode === AuthMode.RESET_PASSWORD && (
            <button
              style={s.linkBtn}
              className="login-link"
              onClick={() => setAuthMode(AuthMode.SIGN_IN)}
            >
              Back to Sign In
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#faf8f5',
    fontFamily: '"Outfit", sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
  },
  logoLink: {
    display: 'block',
    marginBottom: '36px',
  },
  logo: {
    height: '120px',
    objectFit: 'contain',
  },
  card: {
    width: '100%',
    maxWidth: '380px',
    backgroundColor: '#ffffff',
    border: '1px solid #e7e5e4',
    borderRadius: '4px',
    padding: '32px',
  },
  title: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#1c1917',
    textAlign: 'center',
    marginTop: 0,
    marginBottom: '24px',
  },

  /* Google button */
  googleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '10px 16px',
    fontSize: '0.9rem',
    fontFamily: '"Outfit", sans-serif',
    fontWeight: 500,
    color: '#1c1917',
    backgroundColor: '#ffffff',
    border: '1px solid #e7e5e4',
    borderRadius: '3px',
    cursor: 'pointer',
  },

  /* Divider */
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '22px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e7e5e4',
  },
  dividerText: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.7rem',
    color: '#a8a29e',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },

  /* Form */
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9rem',
    fontFamily: '"Outfit", sans-serif',
    color: '#1c1917',
    backgroundColor: '#faf8f5',
    border: '1px solid #e7e5e4',
    borderRadius: '3px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  submitBtn: {
    fontFamily: '"Space Mono", monospace',
    width: '100%',
    padding: '11px 16px',
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#ffffff',
    backgroundColor: '#e8622d',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    marginTop: '4px',
  },

  /* Messages */
  error: {
    fontSize: '0.8rem',
    color: '#dc2626',
    marginTop: '12px',
    marginBottom: 0,
  },
  success: {
    fontSize: '0.8rem',
    color: '#16a34a',
    marginTop: '12px',
    marginBottom: 0,
  },

  /* Links */
  links: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '18px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  linkBtn: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    color: '#e8622d',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    letterSpacing: '0.01em',
  },
};

/* Load fonts */
if (!document.querySelector('#bracketsim-fonts')) {
  const link = document.createElement('link');
  link.id = 'bracketsim-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* Interaction styles */
if (!document.querySelector('#login-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'login-styles';
  sheet.textContent = `
    .login-google-btn:hover { background-color: #f5f5f0 !important; }
    .login-google-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .login-submit-btn:hover:not(:disabled) { background-color: #c2410c !important; }
    .login-input:focus { border-color: #e8622d !important; box-shadow: 0 0 0 2px rgba(232, 98, 45, 0.15); }
    .login-link:hover { text-decoration: underline; }
  `;
  document.head.appendChild(sheet);
}

export default Login;
